/**
 * Unify EpisodeOfCare FHIR model.
 *
 * An episode represents an ongoing clinical care relationship between a client
 * and a provider for a specific therapy service type (e.g. "Individual Therapy
 * with Dr. Nguyen, started Jan 15 2026"). It is the central clinical entity in
 * Unify — appointments, encounters, documentation, and outcomes are all
 * attributed to an episode.
 *
 * A client may have **multiple concurrent episodes** (e.g. individual therapy
 * with one provider and group therapy with another). Each episode is paired 1:1
 * with a ServiceRequest that tracks the intake pipeline.
 *
 * ┌─────────────────────────┬────────────────────────────────────────────────┐
 * │ FHIR field              │ Unify usage                                   │
 * ├─────────────────────────┼────────────────────────────────────────────────┤
 * │ status                  │ planned → active → onhold / finished / cancel │
 * │ type                    │ Therapy service type (CodeSystem below)       │
 * │ patient                 │ Reference<Patient> — the identified client    │
 * │ careManager             │ Reference<Practitioner> — primary provider    │
 * │ team                    │ Reference<CareTeam>[] — care team(s)         │
 * │ account                 │ Reference<Account>[] (single entry)          │
 * │ period                  │ start: care began; end: episode closed        │
 * │ statusHistory           │ Auto audit trail of status transitions        │
 * │ managingOrganization    │ (unused — practice derived from SR.performer) │
 * ├─────────────────────────┼────────────────────────────────────────────────┤
 * │ EXTENSION               │                                               │
 * │ payment-type            │ valueCoding — insurance | self-pay            │
 * │ participants            │ Reference<RelatedPerson>[] for couples/family │
 * │ guarantor (deprecated)  │ valueReference → RelatedPerson                │
 * │ episode-of-care-context │ Used on OTHER resources to link back to EoC   │
 * └─────────────────────────┴────────────────────────────────────────────────┘
 *
 * CodeSystem — therapy-service-type:
 *   individual | couples | family | group | iop |
 *   medication-management | psych-testing
 */
import type { Coding, Condition, EpisodeOfCare, Extension, Reference } from '@medplum/fhirtypes';

import {
  EPISODE_OF_CARE_CONTEXT_EXT,
  GUARANTOR_EXT,
  PARTICIPANTS_EXT,
  PAYMENT_TYPE_EXT,
  PAYMENT_TYPE_SYSTEM,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal unions
// ═══════════════════════════════════════════════════════════════════════

/** Lifecycle statuses used by Unify (subset of FHIR R4). */
export type EpisodeOfCareStatus =
  | 'planned'
  | 'active'
  | 'onhold'
  | 'finished'
  | 'cancelled';

/**
 * Therapy service type codes — shared CodeSystem between
 * `EpisodeOfCare.type` and `ServiceRequest.code`.
 */
export type TherapyServiceTypeCode =
  | 'individual'
  | 'couples'
  | 'family'
  | 'group'
  | 'iop'
  | 'medication-management'
  | 'psych-testing';

/** Payment type codes (insurance | self-pay). */
export type PaymentTypeCode = 'insurance' | 'self-pay';

// ═══════════════════════════════════════════════════════════════════════
//  Standard field helpers
// ═══════════════════════════════════════════════════════════════════════

/** Therapy service type coding from `type[0]`. */
export function getServiceTypeCoding(resource: EpisodeOfCare): Coding | undefined {
  return resource.type?.[0]?.coding?.find(
    (c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM,
  );
}

/** Therapy service type code string (e.g. `"individual"`). */
export function getServiceTypeCode(resource: EpisodeOfCare): string | undefined {
  return getServiceTypeCoding(resource)?.code;
}

/**
 * Statuses under which an episode still occupies the patient's "slot" for a therapy type.
 * `finished`, `cancelled`, and `entered-in-error` are terminal.
 */
export const OPEN_EPISODE_OF_CARE_STATUSES: ReadonlyArray<NonNullable<EpisodeOfCare['status']>> = [
  'planned',
  'waitlist',
  'active',
  'onhold',
];

export function isOpenEpisodeOfCare(resource: EpisodeOfCare): boolean {
  return resource.status !== undefined && OPEN_EPISODE_OF_CARE_STATUSES.includes(resource.status);
}

/**
 * Therapy types that may legitimately have several concurrent open episodes for one patient
 * (one per cohort). Every other type is unique per patient while open.
 */
const MULTI_EPISODE_THERAPY_TYPES: ReadonlySet<string> = new Set(['group']);

export function therapyTypeAllowsMultipleOpenEpisodes(therapyTypeCode: string): boolean {
  return MULTI_EPISODE_THERAPY_TYPES.has(therapyTypeCode);
}

/**
 * The open episode that already covers `therapyTypeCode` for this patient, if creating another
 * would be a duplicate. Returns undefined for group therapy (cohort-scoped) and when none is open.
 */
export function findConflictingOpenEpisode(
  episodes: EpisodeOfCare[],
  therapyTypeCode: string,
): EpisodeOfCare | undefined {
  if (therapyTypeAllowsMultipleOpenEpisodes(therapyTypeCode)) return undefined;
  return episodes.find((e) => isOpenEpisodeOfCare(e) && getServiceTypeCode(e) === therapyTypeCode);
}

/** Primary provider responsible for this episode. */
export function getCareManagerRef(resource: EpisodeOfCare): Reference | undefined {
  return resource.careManager;
}

/** CareTeam references providing care for this episode. */
export function getTeamRefs(resource: EpisodeOfCare): Reference[] {
  return resource.team ?? [];
}

/**
 * CareTeam refs suitable for clinical-note `meta.accounts` stamping.
 * Session clinical notes stamp only these (EoC-scoped), not every Patient panel.
 */
export function careTeamAccountRefsFromEpisode(resource: EpisodeOfCare): Reference[] {
  const seen = new Set<string>();
  const out: Reference[] = [];
  for (const ref of getTeamRefs(resource)) {
    const key = ref.reference?.trim();
    if (!key?.startsWith('CareTeam/') || seen.has(key)) continue;
    seen.add(key);
    out.push({ reference: key, ...(ref.display ? { display: ref.display } : {}) });
  }
  return out;
}

/** Billing/financial Account (stored as sole entry in `account[]`). */
export function getAccountRef(resource: EpisodeOfCare): Reference | undefined {
  return resource.account?.[0];
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension getters
// ═══════════════════════════════════════════════════════════════════════

/** Payment type coding (insurance | self-pay). */
export function getPaymentType(resource: EpisodeOfCare): Coding | undefined {
  return resource.extension?.find((e) => e.url === PAYMENT_TYPE_EXT)?.valueCoding;
}

/** Payment type code string. */
export function getPaymentTypeCode(resource: EpisodeOfCare): PaymentTypeCode | undefined {
  return getPaymentType(resource)?.code as PaymentTypeCode | undefined;
}

/**
 * Other episode participants (e.g. spouse in couples therapy, family
 * members in family therapy). Each occurrence is a separate extension
 * entry with `valueReference → RelatedPerson`.
 */
export function getParticipantRefs(resource: EpisodeOfCare): Reference[] {
  return (
    resource.extension
      ?.filter((e) => e.url === PARTICIPANTS_EXT)
      .map((e) => e.valueReference!)
      .filter(Boolean) ?? []
  );
}

/**
 * @deprecated Removed per PRD 2026-04-27. Use billing Account instead.
 * Billing guarantor (valueReference → RelatedPerson).
 */
export function getGuarantorRef(resource: EpisodeOfCare): Reference | undefined {
  return resource.extension?.find((e) => e.url === GUARANTOR_EXT)?.valueReference;
}

/**
 * Episode-of-care context reference. This extension is primarily used on
 * *other* resources (Appointment, Task) to link back to an EpisodeOfCare;
 * included here for completeness when reading the extension from an EoC.
 */
export function getEpisodeOfCareContextRef(resource: EpisodeOfCare): Reference | undefined {
  return resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference;
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension setters (mutate in place)
// ═══════════════════════════════════════════════════════════════════════

/** Set or remove the payment-type extension. */
export function setPaymentType(resource: EpisodeOfCare, code: PaymentTypeCode | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== PAYMENT_TYPE_EXT);
  if (!code) {
    resource.extension = others.length ? others : undefined;
    return;
  }
  const display = code === 'insurance' ? 'Insurance' : 'Self-Pay';
  resource.extension = [
    ...others,
    {
      url: PAYMENT_TYPE_EXT,
      valueCoding: { system: PAYMENT_TYPE_SYSTEM, code, display },
    },
  ];
}

/** Replace all participant RelatedPerson refs on the episode. */
export function setParticipantRefs(resource: EpisodeOfCare, refs: Reference[]): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== PARTICIPANTS_EXT);
  const entries: Extension[] = refs.map((ref) => ({
    url: PARTICIPANTS_EXT,
    valueReference: ref,
  }));
  resource.extension = [...others, ...entries].length ? [...others, ...entries] : undefined;
}

/**
 * @deprecated Removed per PRD 2026-04-27.
 * Set or remove the billing guarantor extension.
 */
export function setGuarantorRef(resource: EpisodeOfCare, ref: Reference | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== GUARANTOR_EXT);
  resource.extension = ref
    ? [...others, { url: GUARANTOR_EXT, valueReference: ref }]
    : others.length ? others : undefined;
}

/** Set or remove the episode-of-care-context extension. */
export function setEpisodeOfCareContextRef(
  resource: EpisodeOfCare,
  ref: Reference | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== EPISODE_OF_CARE_CONTEXT_EXT);
  resource.extension = ref
    ? [...others, { url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: ref }]
    : others.length ? others : undefined;
}

// ═══════════════════════════════════════════════════════════════════════
//  Diagnosis (EpisodeOfCare.diagnosis[] → Condition refs)
// ═══════════════════════════════════════════════════════════════════════

export interface EpisodeDiagnosisEntry {
  conditionRef: Reference<Condition>;
  rank: number;
  roleCode?: string;
}

/** Condition references from EpisodeOfCare.diagnosis[], ordered by rank. */
export function getDiagnosisConditionRefs(resource: EpisodeOfCare): Reference<Condition>[] {
  const entries = [...(resource.diagnosis ?? [])].sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  );
  return entries
    .map((entry) => entry.condition)
    .filter((ref): ref is Reference<Condition> => Boolean(ref?.reference));
}

/** Replace EpisodeOfCare.diagnosis[] with ranked Condition references. */
export function setDiagnosisConditions(resource: EpisodeOfCare, entries: EpisodeDiagnosisEntry[]): void {
  resource.diagnosis = entries.map((entry) => ({
    condition: entry.conditionRef,
    rank: entry.rank,
    role: entry.roleCode
      ? {
          coding: [{
            system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
            code: entry.roleCode,
          }],
        }
      : {
          coding: [{
            system: 'http://terminology.hl7.org/CodeSystem/diagnosis-role',
            code: 'AD',
            display: 'Admission diagnosis',
          }],
        },
  }));
}
